// PCM16 encode/decode helpers + base64 conversion.
//
// OpenAI Realtime exchanges audio as base64-encoded PCM16. The browser
// captures float samples (Float32Array) from the AudioWorklet and we
// have to convert in both directions:
//
//   - encode: Float32 mic samples → PCM16 → base64 → OpenAI WS
//   - decode: base64 → PCM16 → AudioBuffer → Web Audio playback queue
export function base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < len; i++)
        view[i] = binary.charCodeAt(i);
    return buffer;
}
export function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
// Float32 [-1, 1] → 16-bit signed PCM, returned as base64 ready to send.
export function float32ToPcm16Base64(float32) {
    const pcm16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return arrayBufferToBase64(pcm16.buffer);
}
export function float32ToPcm16ArrayBuffer(float32) {
    const pcm16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return pcm16.buffer;
}
export function resampleMonoFloat32(input, sourceRate, targetRate) {
    var _a;
    if (!input.length || sourceRate === targetRate) {
        return input;
    }
    const outputLength = Math.max(1, Math.round((input.length * targetRate) / sourceRate));
    const output = new Float32Array(outputLength);
    const rateRatio = sourceRate / targetRate;
    for (let index = 0; index < outputLength; index++) {
        const start = index * rateRatio;
        const end = Math.min(input.length, (index + 1) * rateRatio);
        const startIndex = Math.floor(start);
        const endIndex = Math.min(input.length, Math.ceil(end));
        let sum = 0;
        let count = 0;
        for (let sampleIndex = startIndex; sampleIndex < endIndex; sampleIndex++) {
            sum += input[sampleIndex];
            count += 1;
        }
        if (count > 0) {
            output[index] = sum / count;
        }
        else {
            output[index] = (_a = input[Math.min(startIndex, input.length - 1)]) !== null && _a !== void 0 ? _a : 0;
        }
    }
    return output;
}
// base64 PCM16 → AudioBuffer. ``sampleRate`` defaults to 24kHz which is
// what OpenAI Realtime emits.
export function pcm16Base64ToAudioBuffer(base64, ctx, sampleRate = 24000) {
    const arrayBuffer = base64ToArrayBuffer(base64);
    const pcm16 = new Int16Array(arrayBuffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 0x8000;
    }
    const audioBuffer = ctx.createBuffer(1, float32.length, sampleRate);
    audioBuffer.copyToChannel(float32, 0);
    return audioBuffer;
}
